import { Link, useParams, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { bySlug } from "@/lib/omSectors";
import Footer from "@/components/Footer";
import { useMeta } from "@/lib/useMeta";
import { SectorNav } from "@/pages/om/SectorNav";
import { SectorLeadForm } from "@/components/om/SectorLeadForm";
import { NAVY, CARD, GOLD, MUTED, GREEN, AMBER, RED, chartTheme } from "@/components/om/OmBits";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...p} />;
};

const statusColor = (s) => (s === "Operational" ? GREEN : s === "Under Maintenance" ? AMBER : RED);

export default function OmSectorPage() {
  const { slug } = useParams();
  const s = bySlug(slug);
  useMeta({
    title: s ? `${s.name} O&M Software | Plan-Sarthi Operations & Maintenance` : "O&M Sectors | Plan-Sarthi",
    description: s ? `${s.subtitle} Asset register, daily monitoring, preventive and corrective maintenance, spares and compliance reporting for ${s.name.toLowerCase()} operations in India.` : "",
    path: `/om/sectors/${slug}`,
  });

  if (!s) return <Navigate to="/om/sectors" replace />;

  return (
    <div style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid={`sector-page-${s.slug}`}>
      <SectorNav />

      {/* 1 — hero */}
      <section className="relative" style={{ minHeight: "78vh" }} data-testid="sector-hero">
        <img src={s.hero} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,22,40,0.6) 0%, rgba(10,22,40,0.72) 60%, rgba(10,22,40,0.96) 100%)" }} />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 flex flex-col justify-center" style={{ minHeight: "78vh" }}>
          <h1 className="text-white font-bold leading-[1.06] text-4xl sm:text-5xl" style={{ fontSize: undefined }}>{s.name}</h1>
          <p className="mt-4 text-[15px] sm:text-[18px] max-w-2xl" style={{ color: GOLD }}>{s.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/contact" className="text-center font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8, padding: "12px 28px" }} data-testid="sector-hero-demo-btn">Book a Demo</Link>
            <Link to="/signup" className="text-center font-semibold border" style={{ borderColor: GOLD, color: GOLD, borderRadius: 8, padding: "12px 28px" }} data-testid="sector-hero-trial-btn">Start Free Trial</Link>
          </div>
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-3 text-[11.5px] flex items-center gap-1.5" style={{ color: MUTED }} data-testid="sector-breadcrumb">
        <Link to="/" className="hover:underline">Home</Link><span>›</span>
        <Link to="/om/sectors" className="hover:underline">O&amp;M</Link><span>›</span>
        <span style={{ color: GOLD }}>{s.name}</span>
      </div>

      {/* 2 — overview */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-2 gap-8 items-center" data-testid="sector-overview">
        <div>
          <h2 className="text-white font-semibold text-2xl sm:text-3xl">Why {s.name} O&amp;M cannot be run on spreadsheets</h2>
          <p className="mt-4 text-[14px] leading-[1.75]" style={{ color: MUTED }}>{s.overview}</p>
        </div>
        <img src={s.secondary} alt={`${s.name} equipment`} loading="lazy" className="w-full object-cover" style={{ borderRadius: 8, maxHeight: 380, border: "1px solid rgba(201,168,76,0.2)" }} />
      </section>

      {/* 3 — equipment */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-equipment">
        <h2 className="font-semibold text-2xl sm:text-3xl" style={{ color: GOLD, letterSpacing: "0.05em" }}>Equipment and Systems We Monitor</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {s.equipment.map(([icon, name, desc]) => (
            <div key={name} data-testid="sector-equipment-item">
              <Icon name={icon} className="w-5 h-5" style={{ color: GOLD }} />
              <h3 className="text-white font-semibold text-[13.5px] mt-2.5 leading-snug">{name}</h3>
              <p className="text-[12px] mt-1 leading-relaxed" style={{ color: MUTED }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4 — features */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-features">
        <h2 className="text-white font-semibold text-2xl sm:text-3xl">How Plan-Sarthi Powers {s.name} O&amp;M</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {s.features.map(([icon, name, desc]) => (
            <div key={name} className="p-5 border" style={{ background: CARD, borderColor: "rgba(201,168,76,0.16)", borderRadius: 8 }}>
              <Icon name={icon} className="w-6 h-6" style={{ color: GOLD }} />
              <h3 className="text-white font-semibold text-[15px] mt-3">{name}</h3>
              <p className="text-[12.5px] mt-1.5 leading-relaxed" style={{ color: MUTED }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5 — dashboard preview */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-dashboard-preview">
        <h2 className="font-semibold text-2xl sm:text-3xl" style={{ color: GOLD, letterSpacing: "0.05em" }}>Live O&amp;M Dashboard — {s.name}</h2>
        <div className="mt-6 p-4 border" style={{ background: CARD, borderColor: "rgba(201,168,76,0.2)", borderRadius: 8 }}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {s.demo.kpi.map(([label, value]) => (
              <div key={label} className="p-3 border" style={{ background: NAVY, borderColor: "rgba(201,168,76,0.14)", borderRadius: 8 }}>
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: MUTED }}>{label}</div>
                <div className="font-semibold mt-1 tabular-nums" style={{ color: GOLD, fontSize: 22 }}>{value}</div>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mt-3">
            <div className="p-3 border" style={{ background: NAVY, borderColor: "rgba(201,168,76,0.14)", borderRadius: 8 }}>
              <div className="text-[11px] mb-2" style={{ color: MUTED }}>{s.demo.chartLabel} — last 6 months</div>
              <div style={{ height: 190 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={s.demo.chart.map(([month, value]) => ({ month, value }))} margin={{ top: 4, right: 8, left: -20 }}>
                    <CartesianGrid stroke={chartTheme.grid} strokeDasharray="2 4" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: MUTED }} />
                    <YAxis tick={{ fontSize: 10, fill: MUTED }} />
                    <Tooltip contentStyle={chartTheme.tooltip} />
                    <Bar dataKey="value" fill={GOLD} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="p-3 border" style={{ background: NAVY, borderColor: "rgba(201,168,76,0.14)", borderRadius: 8 }}>
              <div className="text-[11px] mb-2" style={{ color: MUTED }}>Asset status</div>
              <ul>
                {s.demo.assets.map(([name, status, health], i) => (
                  <li key={name} className="flex items-center gap-2 py-2" style={{ borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                    <span className="text-white text-[12px] flex-1 truncate">{name}</span>
                    <span className="text-[10px] px-2 py-0.5 whitespace-nowrap" style={{ color: statusColor(status), background: `${statusColor(status)}1F`, border: `1px solid ${statusColor(status)}55`, borderRadius: 999 }}>{status}</span>
                    <span className="text-[11px] tabular-nums w-7 text-right" style={{ color: health >= 80 ? GREEN : health >= 50 ? AMBER : RED }}>{health}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 6 — KPIs */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-kpis">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {s.kpis.map(([metric, label]) => (
            <div key={metric} className="p-5 border text-center" style={{ background: CARD, borderColor: "rgba(201,168,76,0.16)", borderRadius: 8 }}>
              <div className="font-bold" style={{ color: GOLD, fontSize: 22 }}>{metric}</div>
              <div className="text-white text-[12.5px] mt-1.5">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 7 — compliance */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-compliance">
        <h2 className="text-white font-semibold text-2xl sm:text-3xl">Regulatory and Safety Compliance</h2>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-7">
          {s.compliance.map((c) => (
            <li key={c} className="flex items-start gap-2.5 text-[13.5px] text-white">
              <Icons.CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} /> {c}
            </li>
          ))}
        </ul>
      </section>

      {/* 8 — testimonial */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-testimonial">
        <h2 className="text-white font-semibold text-2xl sm:text-3xl">What Our Clients Say</h2>
        <figure className="mt-6 p-7 border relative" style={{ background: CARD, borderColor: "rgba(201,168,76,0.16)", borderRadius: 8 }}>
          <span className="absolute select-none" style={{ color: `${GOLD}33`, fontSize: 84, lineHeight: 1, top: 8, left: 16 }}>&ldquo;</span>
          <blockquote className="relative text-white text-[15px] sm:text-[17px] leading-relaxed pl-8">{s.quote[0]}</blockquote>
          <figcaption className="text-[12.5px] mt-4 pl-8" style={{ color: MUTED }}>— {s.quote[1]}</figcaption>
        </figure>
      </section>

      {/* 8.5 — lead capture */}
      <SectorLeadForm sector={s} />

      {/* 9 — CTA strip */}
      <section className="py-12 sm:py-14 px-5 sm:px-8" style={{ background: GOLD }} data-testid="sector-cta-strip">
        <div className="max-w-[1280px] mx-auto text-center">
          <h2 className="font-bold text-2xl sm:text-3xl" style={{ color: NAVY }}>Ready to Modernise Your {s.name} O&amp;M?</h2>
          <p className="text-white mt-3 text-[14px] max-w-2xl mx-auto leading-relaxed">
            Join leading EPC and O&amp;M companies using Plan-Sarthi to cut downtime, stay compliant, and manage assets smarter.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-7">
            <Link to="/signup" className="w-full sm:w-auto text-center font-semibold text-white" style={{ background: NAVY, borderRadius: 8, padding: "12px 28px" }} data-testid="sector-cta-trial-btn">Start Free Trial</Link>
            <Link to="/contact" className="w-full sm:w-auto text-center font-semibold border-2" style={{ borderColor: NAVY, color: NAVY, borderRadius: 8, padding: "10px 28px" }} data-testid="sector-cta-expert-btn">Talk to an Expert</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
